import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import type { WeeklyProfitabilityResponse, WeeklyProfitabilityRow } from '@accounting/shared';
import { useWeeklyProfitability } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import { ExportButtons } from '../../components/reports/ExportButtons';
import { RefreshButton } from '../../components/reports/RefreshButton';
import { formatCurrency, formatPctOfTotal, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/weekly-profitability')({
  component: WeeklyProfitabilityPage,
});

type View = 'year' | 'quarter' | 'month';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (year: number, month: number) => new Date(year, month, 0).getDate();

/** The date window for the chosen year / quarter / month (month is 1-12). */
function periodRange(view: View, year: number, quarter: number, month: number) {
  const [firstMonth, lastMonth] =
    view === 'year' ? [1, 12] : view === 'quarter' ? [quarter * 3 - 2, quarter * 3] : [month, month];
  return {
    from: `${year}-${pad(firstMonth)}-01`,
    to: `${year}-${pad(lastMonth)}-${pad(lastDay(year, lastMonth))}`,
  };
}

/** "Jan 8 – Jan 14". The year is already chosen in the filter. */
function shortDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// The filter outlives the page, so coming back from a drilldown lands on the
// same quarter/month instead of resetting to the full current year.
let remembered: { view: View; year: number; quarter: number; month: number } | null = null;

/** Which account codes each money column is made of. */
type Column = 'revenue' | 'cogs' | 'grossProfit' | 'expenses' | 'otherNet' | 'netIncome';
const COLUMN_LABEL: Record<Column, string> = {
  revenue: 'Revenue',
  cogs: 'COGS',
  grossProfit: 'Gross Profit',
  expenses: 'Expenses',
  otherNet: 'Other',
  netIncome: 'Net Income',
};
function codesFor(column: Column, c: WeeklyProfitabilityResponse['codes']): string[] {
  switch (column) {
    case 'revenue': return c.revenue;
    case 'cogs': return c.cogs;
    case 'grossProfit': return [...c.revenue, ...c.cogs];
    case 'expenses': return c.expenses;
    case 'otherNet': return c.other;
    case 'netIncome': return [...c.revenue, ...c.cogs, ...c.expenses, ...c.other];
  }
}

function WeeklyProfitabilityPage() {
  const selection = useActiveSelection();
  const navigate = useNavigate();
  const now = new Date();
  const [view, setView] = useState<View>(remembered?.view ?? 'year');
  const [year, setYear] = useState(remembered?.year ?? now.getFullYear());
  const [quarter, setQuarter] = useState(remembered?.quarter ?? Math.ceil((now.getMonth() + 1) / 3));
  const [month, setMonth] = useState(remembered?.month ?? now.getMonth() + 1);
  remembered = { view, year, quarter, month };

  const period = periodRange(view, year, quarter, month);
  // Stop at today: weeks that have not happened yet would only be a column of
  // zeros under the real ones.
  const today = todayIso();
  const to = period.from <= today && today < period.to ? today : period.to;
  const { data, isLoading, error, refetch, isFetching } = useWeeklyProfitability(selection, period.from, to);

  if (!selection) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  /** A money cell that opens the transactions behind it, for one week or the whole period. */
  const cell = (
    column: Column,
    figures: WeeklyProfitabilityResponse['totals'],
    range: { from: string; to: string },
    label: string,
    style?: React.CSSProperties,
  ) => {
    const codes = data ? codesFor(column, data.codes) : [];
    const text = formatCurrency(figures[column], true);
    return (
      <td style={{ textAlign: 'right', ...style }}>
        {codes.length === 0 ? text : (
          <button
            className="amount-link"
            style={{ fontWeight: 'inherit', color: 'inherit' }}
            onClick={() =>
              void navigate({
                to: '/reports/drilldown',
                search: {
                  codes: codes.join(','),
                  label: `${COLUMN_LABEL[column]} — ${label}`,
                  from: range.from,
                  to: range.to,
                  back: '/reports/weekly-profitability',
                },
              })
            }
          >
            {text}
          </button>
        )}
      </td>
    );
  };
  const weekLabel = (w: WeeklyProfitabilityRow) => `Week ${w.weekNumber} (${shortDate(w.from)} – ${shortDate(w.to)})`;

  const years = Array.from({ length: 8 }, (_, i) => now.getFullYear() - i);
  const right = { textAlign: 'right' } as const;
  const hasPartial = data?.weeks.some((w) => w.partial) ?? false;

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Weekly Profitability
          {selection === CONSOLIDATED && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
        </h1>
        <div style={{ fontSize: 13, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>
            Year{' '}
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label>
            Show{' '}
            <select value={view} onChange={(e) => setView(e.target.value as View)}>
              <option value="year">Full year</option>
              <option value="quarter">Quarter</option>
              <option value="month">Month</option>
            </select>
          </label>
          {view === 'quarter' && (
            <select aria-label="Quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
              {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
            </select>
          )}
          {view === 'month' && (
            <select aria-label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          )}
          <RefreshButton onRefresh={() => void refetch()} isFetching={isFetching} />
          <ExportButtons selection={selection} type="weekly_profitability" params={{ from: period.from, to }} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && (
        <div className="card" style={{ padding: 20, overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Dates</th>
                <th style={right}>Revenue</th>
                <th style={right}>COGS</th>
                <th style={right}>Gross Profit</th>
                <th style={right}>Expenses</th>
                <th style={right}>Other</th>
                <th style={right}>Net Income</th>
                <th style={right}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {data.weeks.map((w) => (
                <tr key={`${w.year}-${w.weekNumber}`}>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Week {w.weekNumber}</td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {shortDate(w.from)} – {shortDate(w.to)}
                    {w.partial && <span title="Fewer than 7 days fall inside this period"> *</span>}
                  </td>
                  {cell('revenue', w, w, weekLabel(w))}
                  {cell('cogs', w, w, weekLabel(w))}
                  {cell('grossProfit', w, w, weekLabel(w))}
                  {cell('expenses', w, w, weekLabel(w))}
                  {cell('otherNet', w, w, weekLabel(w))}
                  {cell('netIncome', w, w, weekLabel(w), {
                    fontWeight: 600,
                    color: w.netIncome.startsWith('-') ? 'var(--danger, #c23b22)' : undefined,
                  })}
                  <td className="muted" style={right}>{formatPctOfTotal(w.netIncome, w.revenue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={2}>Total</td>
                {(['revenue', 'cogs', 'grossProfit', 'expenses', 'otherNet', 'netIncome'] as const).map((c) => (
                  <Fragment key={c}>{cell(c, data.totals, data, 'Total')}</Fragment>
                ))}
                <td style={right}>{formatPctOfTotal(data.totals.netIncome, data.totals.revenue)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            Weeks are counted from January 1 — Week 1 is Jan 1–7, Week 2 is Jan 8–14 — so a week number is the
            same dates every year.
            {hasPartial && ' * A short week: only the days inside the selected period are counted, so the total ties to the Income Statement.'}
          </p>
        </div>
      )}
    </div>
  );
}
